import React, { useState, useEffect } from 'react';
import leadersService, { Leader, LeaderRequest } from '../services/leadersService';
import DeleteConfirmationModal from './DeleteConfirmationModal';

const LeadersManagement: React.FC = () => {
  const [leaders, setLeaders] = useState<Leader[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');

  // Form state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<LeaderRequest>({
    name: '',
    designation: '',
    image: '',
    sort_order: 0,
    is_visible: true
  });

  // Image cropper state
  const [imagePreview, setImagePreview] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | null>(null);

  // Delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // Drag and drop state
  const [draggedItem, setDraggedItem] = useState<Leader | null>(null);

  useEffect(() => {
    loadLeaders();
  }, []);

  const loadLeaders = async () => {
    setLoading(true);
    try {
      const response = await leadersService.getAll();
      if (response.success) {
        setLeaders(response.data);
      } else {
        showMessage('Failed to load leaders', 'error');
      }
    } catch (error) {
      console.error('Error loading leaders:', error);
      showMessage('Error loading leaders', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg: string, type: 'success' | 'error') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showMessage('Please select an image file', 'error');
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        showMessage('Image size must be less than 25MB', 'error');
        return;
      }
      setImageFile(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.designation.trim()) {
      showMessage('Please fill in all required fields', 'error');
      return;
    }

    setSaving(true);
    try {
      let response;
      if (editingId) {
        response = await leadersService.update(editingId, formData, imageFile || undefined);
      } else {
        response = await leadersService.create(formData, imageFile || undefined);
      }

      if (response.success) {
        showMessage(editingId ? 'Leader updated successfully' : 'Leader created successfully', 'success');
        resetForm();
        loadLeaders();
      } else {
        showMessage(response.message || 'Failed to save leader', 'error');
      }
    } catch (error) {
      console.error('Error saving leader:', error);
      showMessage('Error saving leader', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (leader: Leader) => {
    setEditingId(leader.id);
    setFormData({
      name: leader.name,
      designation: leader.designation,
      image: leader.image || '',
      sort_order: leader.sort_order,
      is_visible: leader.is_visible
    });
    setImagePreview(leader.image || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;

    setSaving(true);
    try {
      const response = await leadersService.delete(deleteId);
      if (response.success) {
        showMessage('Leader deleted successfully', 'success');
        loadLeaders();
      } else {
        showMessage(response.message || 'Failed to delete leader', 'error');
      }
    } catch (error) {
      console.error('Error deleting leader:', error);
      showMessage('Error deleting leader', 'error');
    } finally {
      setSaving(false);
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const handleToggleVisibility = async (id: number) => {
    setSaving(true);
    try {
      const response = await leadersService.toggleVisibility(id);
      if (response.success) {
        showMessage('Visibility toggled successfully', 'success');
        loadLeaders();
      } else {
        showMessage(response.message || 'Failed to toggle visibility', 'error');
      }
    } catch (error) {
      console.error('Error toggling visibility:', error);
      showMessage('Error toggling visibility', 'error');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      name: '',
      designation: '',
      image: '',
      sort_order: 0,
      is_visible: true
    });
    setImagePreview('');
    setImageFile(null);
  };

  // Drag and drop handlers
  const handleDragStart = (leader: Leader) => {
    setDraggedItem(leader);
  };

  const handleDragOver = (e: React.DragEvent, targetLeader: Leader) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.id === targetLeader.id) return;

    const newLeaders = [...leaders];
    const draggedIndex = newLeaders.findIndex(l => l.id === draggedItem.id);
    const targetIndex = newLeaders.findIndex(l => l.id === targetLeader.id);

    newLeaders.splice(draggedIndex, 1);
    newLeaders.splice(targetIndex, 0, draggedItem);

    // Update sort orders
    const updatedLeaders = newLeaders.map((leader, index) => ({
      ...leader,
      sort_order: index
    }));

    setLeaders(updatedLeaders);
  };

  const handleDragEnd = async () => {
    if (!draggedItem) return;

    const sortOrders = leaders.map((leader, index) => ({
      id: leader.id,
      sort_order: index
    }));

    try {
      const response = await leadersService.updateSortOrders(sortOrders);
      if (response.success) {
        showMessage('Order updated successfully', 'success');
      } else {
        showMessage('Failed to update order', 'error');
        loadLeaders(); // Reload to reset order
      }
    } catch (error) {
      console.error('Error updating order:', error);
      showMessage('Error updating order', 'error');
      loadLeaders();
    }

    setDraggedItem(null);
  };

  return (
    <div className="leaders-management">
      {/* Message */}
      {message && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '20px',
          borderRadius: '4px',
          backgroundColor: messageType === 'success' ? '#d4edda' : '#f8d7da',
          color: messageType === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${messageType === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {message}
        </div>
      )}

      {/* Form */}
      <div style={{
        backgroundColor: '#fff',
        padding: '24px',
        borderRadius: '8px',
        marginBottom: '24px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '20px', fontSize: '18px', fontWeight: '600' }}>
          {editingId ? 'Edit Leader' : 'Add New Leader'}
        </h3>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Name <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter leader name"
                disabled={saving}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                required
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Designation <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <input
                type="text"
                value={formData.designation}
                onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                placeholder="Enter designation"
                disabled={saving}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                required
              />
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
              Leader Photo
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              disabled={saving}
              style={{ display: 'none' }}
              id="leader-image-upload"
            />
            <label
              htmlFor="leader-image-upload"
              style={{
                display: 'inline-block',
                padding: '10px 20px',
                backgroundColor: '#f8f9fa',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              Choose Image
            </label>
            {imagePreview && (
              <div style={{ marginTop: '12px' }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    maxWidth: '200px',
                    maxHeight: '200px',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}
                />
              </div>
            )}
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.is_visible}
                onChange={(e) => setFormData({ ...formData, is_visible: e.target.checked })}
                disabled={saving}
              />
              <span style={{ fontSize: '14px' }}>Show on homepage</span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '10px 24px',
                backgroundColor: '#00a0e3',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1
              }}
            >
              {saving ? 'Saving...' : editingId ? 'Update Leader' : 'Add Leader'}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                disabled={saving}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: saving ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Leaders List */}
      <div style={{
        backgroundColor: '#fff',
        padding: '24px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '20px', fontSize: '18px', fontWeight: '600' }}>
          Leaders List ({leaders.length})
        </h3>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
            Loading leaders...
          </div>
        ) : leaders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
            No leaders added yet. Add your first leader above.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '16px' }}>
            {leaders.map((leader) => (
              <div
                key={leader.id}
                draggable
                onDragStart={() => handleDragStart(leader)}
                onDragOver={(e) => handleDragOver(e, leader)}
                onDragEnd={handleDragEnd}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '16px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  backgroundColor: '#f8f9fa',
                  cursor: 'move'
                }}
              >
                {/* Drag Handle */}
                <div style={{ cursor: 'grab', color: '#6c757d' }}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <circle cx="7" cy="5" r="1.5" />
                    <circle cx="13" cy="5" r="1.5" />
                    <circle cx="7" cy="10" r="1.5" />
                    <circle cx="13" cy="10" r="1.5" />
                    <circle cx="7" cy="15" r="1.5" />
                    <circle cx="13" cy="15" r="1.5" />
                  </svg>
                </div>

                {/* Image */}
                <div style={{
                  width: '80px',
                  height: '100px',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  backgroundColor: '#e9ecef',
                  flexShrink: 0
                }}>
                  {leader.image ? (
                    <img
                      src={leader.image}
                      alt={leader.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#6c757d',
                      fontSize: '12px'
                    }}>
                      No Image
                    </div>
                  )}
                </div>

                {/* Info */}
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600' }}>
                    {leader.name}
                  </h4>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#6c757d' }}>
                    {leader.designation}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      backgroundColor: leader.is_visible ? '#d4edda' : '#f8d7da',
                      color: leader.is_visible ? '#155724' : '#721c24'
                    }}>
                      {leader.is_visible ? 'Visible' : 'Hidden'}
                    </span>
                    <span style={{ color: '#6c757d' }}>
                      Order: {leader.sort_order}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <button
                    onClick={() => handleToggleVisibility(leader.id)}
                    disabled={saving}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '13px',
                      cursor: saving ? 'not-allowed' : 'pointer'
                    }}
                    title={leader.is_visible ? 'Hide' : 'Show'}
                  >
                    {leader.is_visible ? '👁️' : '👁️‍🗨️'}
                  </button>
                  <button
                    onClick={() => handleEdit(leader)}
                    disabled={saving}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#00a0e3',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '13px',
                      cursor: saving ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(leader.id)}
                    disabled={saving}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#dc3545',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '13px',
                      cursor: saving ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={confirmDelete}
          title="Delete Leader"
          message="Are you sure you want to delete this leader? This action cannot be undone."
        />
      )}
    </div>
  );
};

export default LeadersManagement;
